<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col sm="12">
        <b-form @submit.prevent="submit">
          <b-card>
            <div slot="header">
              <strong>{{ $t('messages.applicantInformation') }}</strong>
            </div>
            <b-form-group>
              <label for="applicant.first_name">{{ $t('messages.firstName') }}*</label>
              <b-form-input
                v-validate="'required'"
                v-model="applicant.first_name"
                name="applicant.first_name"
                type="text"
                :state="validateState('applicant.first_name')"
                :data-vv-as="$t('messages.firstName')"
                :placeholder="$t('messages.enterFirstName')"></b-form-input>
            </b-form-group>
            <b-form-group>
              <label for="applicant.last_name">{{ $t('messages.lastName') }}*</label>
              <b-form-input
                type="text"
                v-validate="'required'"
                :state="validateState('applicant.last_name')"
                name="applicant.last_name"
                :data-vv-as="$t('messages.lastName')"
                v-model="applicant.last_name"
                :placeholder="$t('messages.enterLastName')"></b-form-input>
            </b-form-group>
            <b-form-group :label="`${$t('messages.phoneNumber')}*`" description="ex. (999) 999-9999">
                <masked-input
                  type="input"
                  name="applicant.phone_number"
                  @keyup="phoneTouched = true"
                  :class="'form-control ' + phoneValidationCssClass"
                  v-model="applicant.phone_number"
                  :mask="['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]"
                  :guide="true"
                  :placeholder="$t('messages.enterPhoneNumber')"
                  placeholderChar="#">
                </masked-input>
                <div class="invalid-feedback">{{ $t('messages.mustBeValidPhone') }}</div>
            </b-form-group>
            <b-form-group>
              <label for="applicant.email_address">Email*</label>
              <b-form-input
                type="email"
                v-validate="'required|email'"
                :state="validateState('applicant.email_address')"
                name="applicant.email_address"
                data-vv-as="Email"
                v-model="applicant.email_address"
                :placeholder="$t('messages.enterEmail')"></b-form-input>
            </b-form-group>
            <b-row
              :class="{ 'tecobi-credit-app-residence': true, 'valid-residence': (validateState(`applicant.residence_${index}_years`) && validateState(`applicant.residence_${index}_months`)) }"
              v-for="(x, index) in [].constructor(residencesNeeded)"
              :key="index">
              <b-col sm="12">
                <b-form-group>
                  <label :for="`applicant.residence_${index}_address`">{{ index > 0 ? $t('messages.priorAddress') : $t('messages.permanentAddress') }} <em v-show="index > 0">({{ $t('messages.weNeedYears', { num: totalYearsNeeded }) }})</em></label>
                  <b-form-input
                    type="text"
                    v-validate="'required'"
                    :state="validateState(`applicant.residence_${index}_address`)"
                    :name="`applicant.residence_${index}_address`"
                    data-vv-as="Address"
                    v-model="applicant[`residence_${index}_address`]"
                    placeholder="Enter your address"></b-form-input>
                </b-form-group>
                <b-row>
                  <b-col sm="6">
                    <b-form-group>
                      <label :for="`applicant.residence_${index}_city`">{{ $t('messages.city') }}</label>
                      <b-form-input
                        type="text"
                        v-validate="'required'"
                        :state="validateState(`applicant.residence_${index}_city`)"
                        :name="`applicant.residence_${index}_city`"
                        :data-vv-as="$t('messages.city')"
                        v-model="applicant[`residence_${index}_city`]"
                        :placeholder="$t('messages.enterCity')"></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col sm="3">
                    <b-form-group>
                      <label :for="`applicant.residence_${index}_state`">{{ $t('messages.state') }}</label>
                      <b-form-select
                        :name="`applicant.residence_${index}_state`"
                        :data-vv-as="$t('messages.state')"
                        :options="[{text: $t('messages.selectState'), value: null}].concat(stateOptions)"
                        v-model="applicant[`residence_${index}_state`]"
                        v-validate="'required|length:2|max:2'"
                        :state="validateState(`applicant.residence_${index}_state`)"
                        type="text"
                        :placeholder="$t('messages.state')"></b-form-select>
                    </b-form-group>
                  </b-col>
                  <b-col sm="3">
                    <b-form-group>
                      <label :for="`applicant.residence_${index}_postal_code`">{{ $t('messages.postalCode') }}</label>
                      <b-form-input
                        type="tel"
                        v-validate="'required|length:5|numeric'"
                        maxlength="5"
                        :state="validateState(`applicant.residence_${index}_postal_code`)"
                        :name="`applicant.residence_${index}_postal_code`"
                        :data-vv-as="$t('messages.postalCode')"
                        v-model="applicant[`residence_${index}_postal_code`]"
                        :placeholder="$t('messages.postalCode')"></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>

              <b-col sm="6" v-if="index === 0">
                <b-form-group>
                  <label :for="`applicant.residence_${index}_rent_own`">{{ $t('messages.doYouRentOwn') }}*</label>
                  <b-form-select
                    :name="`applicant.residence_${index}_rent_own`"
                    data-vv-as="Rent/Own"
                    :options="rentOwnOptions"
                    v-model="applicant[`residence_${index}_rent_own`]"
                    v-validate="'required'"
                    :state="validateState(`applicant.residence_${index}_rent_own`)"
                    type="text"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col sm="6" v-if="index === 0">
                <b-form-group>
                <label :for="`applicant.residence_${index}_rent_mortgage_amount`">{{ $t('messages.rentMortgageMonthlyAmount') }}*</label>
                  <b-input-group prepend="$">
                    <b-form-input
                      :name="`applicant.residence_${index}_rent_mortgage_amount`"
                      data-vv-as="Residence Rent/Mortgage amount"
                      @keypress="isNumber($event)"
                      :placeholder="$t('messages.enterHousingPayment')"
                      v-model="applicant[`residence_${index}_rent_mortgage_amount`]"
                      v-validate="'required|numeric'"
                      :state="validateState(`applicant.residence_${index}_rent_mortgage_amount`)"
                      type="tel"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col sm="6">
                <b-form-group>
                  <label :for="`applicant.residence_${index}_years`">{{ $t('messages.timeAtResidence') }}</label>
                  <b-form-select
                    :name="`applicant.residence_${index}_years`"
                    data-vv-as="Residence Years"
                    :options="yearsOptions"
                    v-model="applicant[`residence_${index}_years`]"
                    v-validate="'required'"
                    :state="validateState(`applicant.residence_${index}_years`)"
                    type="text"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col sm="6">
                <b-form-group>
                <label :for="`applicant.residence_${index}_months`">&nbsp;</label>
                  <b-form-select
                    :name="`applicant.residence_${index}_months`"
                    data-vv-as="Residence months"
                    :options="monthsOptions"
                    v-model="applicant[`residence_${index}_months`]"
                    v-validate="'required'"
                    :state="validateState(`applicant.residence_${index}_months`)"
                    type="text"></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>
            <div slot="footer">
              <b-button
                :disabled="submitting || !isComplete || veeErrors.any()"
                type="submit"
                size="md"
                variant="primary">
                <i class="fa fa-dot-circle-o"></i>
                Continue</b-button>
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import MaskedInput from 'vue-text-mask'
import { mapActions, mapGetters } from 'vuex';
import stateList from './us-states';
import canadaProvincList from './ca-provinces';
import yearsOptions from './years-options';
import monthsOptions from './months-options';
import rentOwnOptions from './rent-own-options';
import VueNotifications from 'vue-notifications';

function formatUsPhone(phone) {

    var phoneTest = new RegExp(/^((\+1)|1)? ?\(?(\d{3})\)?[ .-]?(\d{3})[ .-]?(\d{4})( ?(ext\.? ?|x)(\d*))?$/);

    phone = phone.trim();
    var results = phoneTest.exec(phone);
    if (results !== null && results.length > 8) {

        return "(" + results[3] + ") " + results[4] + "-" + results[5] + (typeof results[8] !== "undefined" ? " x" + results[8] : "");

    }
    else {
         return phone;
    }
}

const stateOptions = stateList.concat(canadaProvincList);

const today = new Date()

export default {
  name: 'advanced-forms',
  components: {
    MaskedInput,
  },
  beforeMount() {
      const lead = this.$store.getters['lead/currentLead'];
      const application = this.$store.getters['creditapps/currentCreditApp'];
      if (application && application.id)  {
        this.applicant = Object.assign({}, application);
      } else if (lead && lead.urlhash) {
        this.applicant.lead = lead.urlhash;
        this.applicant.client = this.$route.params.clientId;
        this.applicant.first_name = lead.first_name;
        this.applicant.last_name = lead.last_name;
        this.applicant.email_address = lead.email_address;
        this.applicant.phone_number = formatUsPhone(lead.phone_number);
        this.applicant.residence_0_city = lead.city;
        this.applicant.residence_0_address = lead.address;
        this.applicant.residence_0_state = lead.state || null;
        this.applicant.residence_0_postal_code = lead.postal_code;
        if(this.$route.params.creditApplicationId) {
            this.applicant.credit_application = this.$route.params.creditApplicationId;
            this.applicant.is_primary = false;
        }
      }
  },
  data() {
    return {
      rentOwnOptions,
      totalYearsNeeded: 2,
      submitting: false,
      applicant: {
        'lead': '',
        'residence_0_state': '',
        'residence_0_rent_own': '',
        'is_simple': true,
        'residence_0_months': null,
        'residence_0_years': null,
        'residence_0_postal_code': '',
        'first_name': '',
        'last_name': '',
        'email_address': '',
        'phone_number': '',
        'residence_0_city': '',
        'residence_0_address': '',
        'residence_0_rent_own': '',
        'residence_0_rent_mortgage_amount': ''
      },
      phoneTouched: false,
      stateOptions,
      yearsOptions,
      monthsOptions,
      residencesNeeded: 1,
      totalResidenceTime: 0,
    }
  },
  notifications: {
    showSuccessMsg: {
      type: VueNotifications.types.success,
      title: 'Success',
      message: 'Successfully saved.'
    },
    showErrorMsg: {
      type: VueNotifications.types.error,
      title: 'Oops',
      message: 'There was an error.'
    }
  },
  computed: {
    ...mapGetters({
        lead: 'lead/currentLead',
    }),
    isComplete() {
      return this.applicant.residence_0_state &&
        this.applicant.residence_0_rent_own &&
        this.applicant.residence_0_months &&
        this.applicant.residence_0_years &&
        this.applicant.residence_0_postal_code &&
        this.applicant.first_name &&
        this.applicant.last_name &&
        this.applicant.email_address &&
        this.applicant.phone_number &&
        this.applicant.residence_0_city &&
        this.applicant.residence_0_address &&
        this.applicant.residence_0_rent_mortgage_amount;
    },
    phoneValidationCssClass () {
      if (!this.phoneTouched) {
        return '';
      }
      const re = /^\(\d{3}\) \d{3}-\d{4}$/
      return re.test(this.applicant.phone_number) ? 'is-valid' : 'is-invalid';
    }
  },
  methods: {
    isNumber(evt) {
      evt = (evt) ? evt : window.event;
      var charCode = (evt.which) ? evt.which : evt.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        evt.preventDefault();;
      } else {
        return true;
      }
    },
    ...mapActions({
        createCreditApp: 'creditapps/createCreditApp',
    }),
    submit() {
        this.submitting = true;
        const applicantKeys = Object.keys(this.applicant);
        applicantKeys.forEach((key) => {
            if (key.startsWith('residence_') && this.applicant[key] === '') {
                delete this.applicant[key];
            }
        })
        this.createCreditApp(this.applicant).then((res) => {
            this.submitting = false;
            this.$router.push({ name: 'ShortStep2', params: {clientId: this.$route.params.clientId, leadId: this.$route.params.leadId} });
        }).catch((err) => {
            this.submitting = false;
            this.showErrorMsg();
        });
    },
    validateState(ref) {
      if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
        return !this.veeErrors.has(ref)
      }
      return null
    }
  }
}
</script>
<style lang="scss">
@media only screen and (max-width: 767px) {
  .card-footer .btn {
    display: block;
    width: 100%;
  }
}
</style>
